import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  Container,
  PageHeader,
  PageTitle,
  ActionToolbar,
  SectionTitle,
  FieldsGrid,
  MainImageContainer,
  AvatarWrapper,
  MenuButton,
  MainImageInfo,
  ImagesGrid,
  ImageCard,
  MenusGrid,
  MenuUrl,
  TextAreaField,
  ContentBody,
  ImagesTabContainer,
  TabContent
} from './Restaurant.styled';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import PageContainer from '../../components/PageContainer/PageContainer';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchRestaurantByAlias, loadReview, selectCurrentRestaurant, selectIsLoading, deleteRestaurant, publishRestaurant, saveRestaurant, selectShouldOpen, selectMessage, setOpen, selectSeverity, backfillRestaurantImages } from './slice';
import TextField from '@mui/material/TextField';
import { Restaurant as RestaurantDTO, RestaurantImage } from './types';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import Backdrop from '@mui/material/Backdrop';
import { Avatar, Menu, MenuItem, Box, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import { IconDots } from '@tabler/icons-react';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { store } from '../../redux/store';
import { okaoAxios } from '../../helpers/axios';

const RestaurantPage: React.FC = () => {
  const { alias } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const restaurantFromServer = useAppSelector(selectCurrentRestaurant);
  const [restaurant, setRestaurant] = useState(restaurantFromServer);
  const openServer = useAppSelector(selectShouldOpen);
  const message = useAppSelector(selectMessage);
  const severity = useAppSelector(selectSeverity);
  const isLoading = useAppSelector(selectIsLoading);

  useEffect(() => {
    setRestaurant(restaurantFromServer);
  }, [restaurantFromServer]);

  useEffect(() => {
    void dispatch(fetchRestaurantByAlias(alias ?? ''));
  }, [dispatch, alias]);

  // Form state
  const [name, setName] = useState(restaurant?.name ?? '');
  const [address1, setAddress1] = useState(restaurant?.address1 ?? '');
  const [address2, setAddress2] = useState(restaurant?.address2 ?? '');
  const [address3, setAddress3] = useState(restaurant?.address3 ?? '');
  const [city, setCity] = useState(restaurant?.city ?? '');
  const [state, setState] = useState(restaurant?.state ?? '');
  const [country, setCountry] = useState(restaurant?.country ?? '');
  const [zipCode, setZipCode] = useState(restaurant?.zipCode ?? '');
  const [phone, setPhone] = useState(restaurant?.phone ?? '');
  const [website, setWebsite] = useState(restaurant?.website ?? '');
  const [reservationUrl, setReservationUrl] = useState(restaurant?.reservationUrl ?? '');
  const [contactName, setContactName] = useState(restaurant?.contactName ?? '');
  const [contactEmail, setContactEmail] = useState(restaurant?.contactEmail ?? '');
  const [description, setDescription] = useState(restaurant?.description ?? '');
  const [tags, setTags] = useState(restaurant?.tags ?? '');
  const [instagram, setInstagram] = useState(restaurant?.instagram ?? '');
  const [facebook, setFacebook] = useState(restaurant?.facebook ?? '');
  const [atmosphere, setAtmosphere] = useState(restaurant?.atmosphere ?? '');
  const [category, setCategory] = useState(restaurant?.category ?? '');
  const [cuisine, setCuisine] = useState(restaurant?.cuisine ?? '');
  const [occasion, setOccasion] = useState(restaurant?.occasion ?? '');
  const [specialFeatures, setSpecialFeatures] = useState(restaurant?.specialFeatures ?? '');
  const [awards, setAwards] = useState(restaurant?.awards ?? '');
  const [knownFor, setKnownFor] = useState(restaurant?.knownFor ?? '');
  const [mainImageUrl, setMainImageUrl] = useState(restaurant?.imageUrl);
  const [images, setImages] = useState(() =>
    restaurant?.restaurantImages != null
      ? [...restaurant.restaurantImages].sort((a, b) => a.order - b.order)
      : []
  );

  // Menu state
  const [mainImageMenuAnchor, setMainImageMenuAnchor] = useState<null | HTMLElement>(null);
  const [additionalImageMenuAnchor, setAdditionalImageMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | undefined>(undefined);
  const [imageTabIndex, setImageTabIndex] = useState(0);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  // Accordion state - all sections expanded by default
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    images: true,
    basic: true,
    address: true,
    contact: true,
    attributes: true,
    content: true,
    menus: true
  });

  const handleAccordionChange = (section: string) => (event: React.SyntheticEvent, isExpanded: boolean): void => {
    setExpandedSections(prev => ({ ...prev, [section]: isExpanded }));
  };

  // File input refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const additionalFileInputRef = useRef<HTMLInputElement>(null);

  // Sync form state when restaurant changes
  useEffect(() => {
    if (restaurant != null) {
      setName(restaurant.name ?? '');
      setAddress1(restaurant.address1 ?? '');
      setAddress2(restaurant.address2 ?? '');
      setAddress3(restaurant.address3 ?? '');
      setCity(restaurant.city ?? '');
      setState(restaurant.state ?? '');
      setCountry(restaurant.country ?? '');
      setZipCode(restaurant.zipCode ?? '');
      setPhone(restaurant.phone ?? '');
      setWebsite(restaurant.website ?? '');
      setReservationUrl(restaurant.reservationUrl ?? '');
      setContactName(restaurant.contactName ?? '');
      setContactEmail(restaurant.contactEmail ?? '');
      setDescription(restaurant.description ?? '');
      setTags(restaurant.tags ?? '');
      setInstagram(restaurant.instagram ?? '');
      setFacebook(restaurant.facebook ?? '');
      setAtmosphere(restaurant.atmosphere ?? '');
      setCategory(restaurant.category ?? '');
      setCuisine(restaurant.cuisine ?? '');
      setOccasion(restaurant.occasion ?? '');
      setSpecialFeatures(restaurant.specialFeatures ?? '');
      setAwards(restaurant.awards ?? '');
      setKnownFor(restaurant.knownFor ?? '');
      setMainImageUrl(restaurant.imageUrl);
      setImages(
        restaurant.restaurantImages != null
          ? [...restaurant.restaurantImages].sort((a, b) => a.order - b.order)
          : []
      );
    }
  }, [restaurant]);

  const setOpenServer = (open: boolean): void => {
    dispatch(setOpen(open));
  };

  // Image API thunks
  const updateRestaurantImage = createAsyncThunk(
    'api/updateRestaurantImage',
    async (formData: FormData) => {
      const auth = store.getState().secure.accessToken ?? '';
      const response = await okaoAxios(auth).post(
        `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/upload/restaurant/${restaurant?.alias ?? ''}/main`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
  );

  const deleteRestaurantImage = createAsyncThunk(
    'api/deleteRestaurantImage',
    async () => {
      const auth = store.getState().secure.accessToken ?? '';
      await okaoAxios(auth).delete(
        `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/upload/restaurant/${restaurant?.alias ?? ''}/main`,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
    }
  );

  const updateRestaurantOtherImage = createAsyncThunk(
    'api/updateRestaurantOtherImage',
    async (data: { formData: FormData; index: number }) => {
      const auth = store.getState().secure.accessToken ?? '';
      const response = await okaoAxios(auth).post(
        `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/upload/restaurant/${restaurant?.restaurantId ?? ''}/other/${data.index}`,
        data.formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
  );

  const deleteRestaurantOtherImage = createAsyncThunk(
    'api/deleteRestaurantOtherImage',
    async (imageIndex: number) => {
      const auth = store.getState().secure.accessToken ?? '';
      const response = await okaoAxios(auth).delete(
        `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/upload/restaurant/${restaurant?.restaurantId ?? ''}/other/${imageIndex}`,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }
  );

  // Menu handlers
  const handleMainImageMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setMainImageMenuAnchor(event.currentTarget);
  };

  const handleMainImageMenuClose = (): void => {
    setMainImageMenuAnchor(null);
  };

  const handleAdditionalImageMenuOpen = (event: React.MouseEvent<HTMLElement>, index: number): void => {
    setAdditionalImageMenuAnchor(event.currentTarget);
    setSelectedImageIndex(index);
  };

  const handleAdditionalImageMenuClose = (): void => {
    setAdditionalImageMenuAnchor(null);
    setSelectedImageIndex(undefined);
  };

  // Edit handlers
  const handleMainImageEdit = (): void => {
    handleMainImageMenuClose();
    fileInputRef.current?.click();
  };

  const handleAdditionalImageEdit = () => {
    handleAdditionalImageMenuClose();
    additionalFileInputRef.current?.click();
  };

  const handleAddAdditionalImage = (): void => {
    if (images.length < 4) {
      setSelectedImageIndex(images.length);
      additionalFileInputRef.current?.click();
    }
  };

  // File change handlers
  const handleMainImageChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file != null) {
      const formData = new FormData();
      formData.append('file', file);
      void dispatch(updateRestaurantImage(formData)).then((result) => {
        if (updateRestaurantImage.fulfilled.match(result)) {
          setMainImageUrl(result.payload.imageUrl);
        }
      });
    }
  };

  const handleAdditionalImageChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file != null && selectedImageIndex !== undefined) {
      const formData = new FormData();
      formData.append('file', file);
      void dispatch(updateRestaurantOtherImage({ formData, index: selectedImageIndex })).then((result) => {
        if (updateRestaurantOtherImage.fulfilled.match(result)) {
          // eslint-disable-next-line
          const payload = result.payload as any;
          setImages(
            payload?.restaurantImages != null
              ? [...payload.restaurantImages].sort((a, b) => a.order - b.order)
              : []
          );
        }
      });
    }
  };

  // Delete handlers
  const handleMainImageDelete = (): void => {
    handleMainImageMenuClose();
    void dispatch(deleteRestaurantImage()).then((result) => {
      if (deleteRestaurantImage.fulfilled.match(result)) {
        setMainImageUrl('');
      }
    });
  };

  const handleAdditionalImageDelete = (): void => {
    handleAdditionalImageMenuClose();
    if (selectedImageIndex !== undefined) {
      void dispatch(deleteRestaurantOtherImage(selectedImageIndex)).then((result) => {
        if (deleteRestaurantOtherImage.fulfilled.match(result)) {
          // eslint-disable-next-line
          const payload = result.payload as any;
          setImages(
            payload?.restaurantImages != null
              ? [...payload.restaurantImages].sort((a, b) => a.order - b.order)
              : []
          );
        }
      });
    }
  };

  // Save restaurant
  const handleSaveRestaurant = (): void => {
    // Create request object with snake_case for API payload (backend expects snake_case)
    // Type assertion needed because Restaurant interface uses camelCase for UI, but API expects snake_case
    const request = {
      restaurantId: restaurant?.restaurantId ?? '',
      alias: restaurant?.alias ?? '',
      name: name ?? '',
      address1: address1 ?? '',
      address2,
      address3,
      city: city ?? '',
      state: state ?? '',
      country: country ?? '',
      phone,
      website,
      reservationUrl: reservationUrl ?? '',
      zip_code: zipCode,
      contact_name: contactName,
      contact_email: contactEmail,
      place_id: restaurant?.placeId ?? '',
      tags,
      description,
      instagram,
      facebook,
      status: restaurant?.status ?? '',
      menus: restaurant?.menus,
      atmosphere,
      category,
      cuisine,
      occasion,
      specialFeatures,
      awards,
      knownFor
    } as unknown as RestaurantDTO;
    void dispatch(saveRestaurant(request));
  };

  // Handle backfill images from Google
  const handleBackfillImages = async (): Promise<void> => {
    if (restaurant?.restaurantId == null) {
      return;
    }

    // Optional confirmation when overwriting
    if (overwriteExisting) {
      const confirmed = window.confirm(
        'This will overwrite existing images for this restaurant with new ones from Google. Continue?'
      );
      if (!confirmed) {
        return;
      }
    }

    try {
      setIsBackfilling(true);
      await dispatch(
        backfillRestaurantImages({
          restaurantId: restaurant.restaurantId,
          overwriteExisting
        })
      ).unwrap();

      // Refresh restaurant data to show new images
      await dispatch(fetchRestaurantByAlias(alias ?? '')).unwrap();
    } catch (error) {
      console.error('Error backfilling images', error);
      // Error is already handled by Redux slice (toast notification)
    } finally {
      setIsBackfilling(false);
    }
  };

  return (
    <PageContainer title={restaurant?.name} open={openServer} setOpen={setOpenServer} message={message} severity={severity}>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading} />
      <Container>
        {/* Header */}
        <PageHeader>
          <PageTitle>{restaurant?.name ?? 'Restaurant Details'}</PageTitle>
          <ActionToolbar>
            <Button variant="contained" size="small" onClick={handleSaveRestaurant}>
              Update
            </Button>
            <Button variant="outlined" size="small" onClick={() => {
              if (restaurant != null) {
                void dispatch(publishRestaurant(restaurant.restaurantId));
              }
            }}>
              Publish
            </Button>
            <Button variant="outlined" size="small" onClick={() => {
              if (restaurant != null) {
                void dispatch(deleteRestaurant(restaurant.restaurantId));
                navigate('/');
              }
            }}>
              Delete
            </Button>
            <Button variant="outlined" size="small" onClick={() => {
              if (restaurant != null) {
                void dispatch(loadReview(restaurant.restaurantId));
              }
            }}>
              Load Reviews
            </Button>
            <Button variant="outlined" size="small" onClick={() => {
              navigate(`/restaurant/${restaurant?.alias}/add-new-menu`);
            }}>
              Add Menu
            </Button>
            <Button variant="outlined" size="small" onClick={() => {
              navigate(`/restaurant/${restaurant?.alias}/reviews`);
            }}>
              Reviews
            </Button>
          </ActionToolbar>
        </PageHeader>

        <ContentBody>
          {/* Images Section with Tabs */}
          <Accordion expanded={expandedSections.images} onChange={handleAccordionChange('images')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionTitle>Images</SectionTitle>
            </AccordionSummary>
            <AccordionDetails>
              <ImagesTabContainer>
              <Tabs
                value={imageTabIndex}
                onChange={(e, newValue) => setImageTabIndex(newValue)}
                sx={{
                  borderBottom: '1px solid #e0e0e0',
                  '& .MuiTab-root': {
                    fontSize: '14px',
                    fontWeight: 500,
                    textTransform: 'none',
                  },
                  '& .Mui-selected': {
                    fontWeight: 600,
                  },
                }}
              >
                <Tab label="Main Image" />
                <Tab label="Additional Images" />
              </Tabs>

              {/* Main Image Tab */}
              {imageTabIndex === 0 && (
                <TabContent>
                  <MainImageContainer>
                    <AvatarWrapper>
                      <Avatar
                        src={
                          mainImageUrl ??
                          'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png'
                        }
                        sx={{ width: 150, height: 150 }}
                        alt="Restaurant main image"
                      />
                      <MenuButton size="small" onClick={handleMainImageMenuOpen} title="Image actions">
                        <IconDots size={16} stroke={2} />
                      </MenuButton>
                      <Menu
                        anchorEl={mainImageMenuAnchor}
                        open={Boolean(mainImageMenuAnchor)}
                        onClose={handleMainImageMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        <MenuItem onClick={handleMainImageEdit}>Edit</MenuItem>
                        <MenuItem onClick={handleMainImageDelete}>Delete</MenuItem>
                      </Menu>
                    </AvatarWrapper>
                    <MainImageInfo>
                      <div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleMainImageChange}
                        />
                      </div>
                    </MainImageInfo>
                  </MainImageContainer>
                </TabContent>
              )}

              {/* Additional Images Tab */}
              {imageTabIndex === 1 && (
                <TabContent>
                  {images != null && images.length > 0 ? (
                    <>
                      <ImagesGrid>
                        {images.map((image: RestaurantImage, index) => (
                          <ImageCard key={index}>
                            <AvatarWrapper>
                              <Avatar
                                src={image.url}
                                sx={{ width: 100, height: 100 }}
                                alt={`Restaurant image ${index + 1}`}
                              />
                              <MenuButton
                                size="small"
                                onClick={(e) => handleAdditionalImageMenuOpen(e, index)}
                                title="Image actions"
                              >
                                <IconDots size={14} stroke={2} />
                              </MenuButton>
                            </AvatarWrapper>
                          </ImageCard>
                        ))}
                      </ImagesGrid>
                      {images.length < 4 && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleAddAdditionalImage}
                        >
                          Add More Images
                        </Button>
                      )}
                      <Menu
                        anchorEl={additionalImageMenuAnchor}
                        open={Boolean(additionalImageMenuAnchor)}
                        onClose={handleAdditionalImageMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        <MenuItem onClick={handleAdditionalImageEdit}>Edit</MenuItem>
                        <MenuItem onClick={handleAdditionalImageDelete}>Delete</MenuItem>
                      </Menu>
                      <input
                        ref={additionalFileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleAdditionalImageChange}
                      />
                    </>
                  ) : (
                    <>
                      <Typography color="text.secondary" sx={{ marginBottom: 2 }}>
                        No additional images yet
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleAddAdditionalImage}
                        disabled={images.length >= 4}
                      >
                        Add Image
                      </Button>
                      <input
                        ref={additionalFileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleAdditionalImageChange}
                      />
                    </>
                  )}
                </TabContent>
              )}

              {/* Backfill Images Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={overwriteExisting}
                      onChange={(e) => {
                        setOverwriteExisting(e.target.checked);
                      }}
                      disabled={isBackfilling}
                    />
                  }
                  label="Overwrite existing images?"
                />
                <Button
                  variant="contained"
                  disabled={isBackfilling}
                  onClick={() => {
                    void handleBackfillImages();
                  }}
                  startIcon={isBackfilling ? <CircularProgress size={16} sx={{ color: 'white' }} /> : undefined}
                >
                  {isBackfilling ? 'Backfilling...' : 'Backfill images from Google'}
                </Button>
              </Box>
            </ImagesTabContainer>
            </AccordionDetails>
          </Accordion>

          {/* Basic Information */}
          <Accordion expanded={expandedSections.basic} onChange={handleAccordionChange('basic')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionTitle>Basic Information</SectionTitle>
            </AccordionSummary>
            <AccordionDetails>
            <FieldsGrid>
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Phone"
                variant="outlined"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Website"
                variant="outlined"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Reservation URL"
                variant="outlined"
                value={reservationUrl}
                onChange={(e) => setReservationUrl(e.target.value)}
                size="small"
              />
            </FieldsGrid>
            </AccordionDetails>
          </Accordion>

          {/* Address */}
          <Accordion expanded={expandedSections.address} onChange={handleAccordionChange('address')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionTitle>Address</SectionTitle>
            </AccordionSummary>
            <AccordionDetails>
            <FieldsGrid>
              <TextField
                fullWidth
                label="Address Line 1"
                variant="outlined"
                value={address1}
                onChange={(e) => setAddress1(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Address Line 2"
                variant="outlined"
                value={address2}
                onChange={(e) => setAddress2(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Address Line 3"
                variant="outlined"
                value={address3}
                onChange={(e) => setAddress3(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="State"
                variant="outlined"
                value={state}
                onChange={(e) => setState(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Zip Code"
                variant="outlined"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Country"
                variant="outlined"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                size="small"
              />
            </FieldsGrid>
            </AccordionDetails>
          </Accordion>

          {/* Contact Information */}
          <Accordion expanded={expandedSections.contact} onChange={handleAccordionChange('contact')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionTitle>Contact Information</SectionTitle>
            </AccordionSummary>
            <AccordionDetails>
            <FieldsGrid>
              <TextField
                fullWidth
                label="Contact Name"
                variant="outlined"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Contact Email"
                variant="outlined"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Instagram"
                variant="outlined"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Facebook"
                variant="outlined"
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
                size="small"
              />
            </FieldsGrid>
            </AccordionDetails>
          </Accordion>

          {/* Restaurant Attributes */}
          <Accordion expanded={expandedSections.attributes} onChange={handleAccordionChange('attributes')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionTitle>Restaurant Attributes</SectionTitle>
            </AccordionSummary>
            <AccordionDetails>
            <FieldsGrid>
              <TextField
                fullWidth
                label="Category"
                variant="outlined"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Cuisine"
                variant="outlined"
                value={cuisine}
                onChange={(e) => setCuisine(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Atmosphere"
                variant="outlined"
                value={atmosphere}
                onChange={(e) => setAtmosphere(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Occasion"
                variant="outlined"
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Special Features"
                variant="outlined"
                value={specialFeatures}
                onChange={(e) => setSpecialFeatures(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Awards"
                variant="outlined"
                value={awards}
                onChange={(e) => setAwards(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Known For"
                variant="outlined"
                value={knownFor}
                onChange={(e) => setKnownFor(e.target.value)}
                size="small"
              />
            </FieldsGrid>
            </AccordionDetails>
          </Accordion>

          {/* Description & Tags */}
          <Accordion expanded={expandedSections.content} onChange={handleAccordionChange('content')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionTitle>Content</SectionTitle>
            </AccordionSummary>
            <AccordionDetails>
            <FieldsGrid>
              <TextAreaField>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={4}
                  variant="outlined"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </TextAreaField>
              <TextAreaField>
                <TextField
                  fullWidth
                  label="Tags"
                  multiline
                  rows={4}
                  variant="outlined"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                />
              </TextAreaField>
            </FieldsGrid>
            </AccordionDetails>
          </Accordion>

          {/* Menus */}
          {(restaurant?.menus?.length ?? 0) > 0 && (
            <Accordion expanded={expandedSections.menus} onChange={handleAccordionChange('menus')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionTitle>Menus</SectionTitle>
              </AccordionSummary>
              <AccordionDetails>
              <MenusGrid>
                {(restaurant?.menus ?? []).map((menu) => (
                  <Card key={menu.menuID}>
                    <CardContent>
                      <Typography variant="h6" component="div">
                        {menu.name}
                      </Typography>
                      <MenuUrl>{menu.url}</MenuUrl>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => navigate(`/restaurant/${alias}/menu/${menu.menuID}`)}
                      >
                        View
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </MenusGrid>
              </AccordionDetails>
            </Accordion>
          )}

        </ContentBody>
      </Container>
    </PageContainer>
  );
};

export default withAuthenticationRequired(RestaurantPage);
